import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db, isFirebaseConfigured } from "../lib/firebase";
import { ManifestShell } from "../components/manifest/CosmicSky";
import CosmicReceipt from "../components/manifest/CosmicReceipt";

// A quiet, unlinked history of past manifestation orders — a drawer of old
// receipts from Universe & Co. Reached by direct link (/manifest/log) so
// placing an order can still feel like "set it and let it go".

// Format a Firestore timestamp (or millis) for display.
const formatWhen = (createdAt) => {
  if (!createdAt) return "Just now";
  const d =
    typeof createdAt.toDate === "function"
      ? createdAt.toDate()
      : new Date(createdAt);
  return d.toLocaleDateString("default", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const Notice = ({ children }) => (
  <p className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 text-center text-sm text-slate-300 backdrop-blur-sm">
    {children}
  </p>
);

const ManifestLog = () => {
  const [orders, setOrders] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!isFirebaseConfigured || !db) {
      setLoaded(true);
      return;
    }
    let active = true;
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, "manifestations"), orderBy("createdAt", "desc"))
        );
        if (!active) return;
        setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch {
        if (active) setError(true);
      } finally {
        if (active) setLoaded(true);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  let body;
  if (!isFirebaseConfigured) {
    body = (
      <Notice>
        Order history needs Firebase to be configured. Add the{" "}
        <code className="text-amber-200">REACT_APP_FIREBASE_*</code> values (see{" "}
        <code className="text-amber-200">.env.example</code>) and reload.
      </Notice>
    );
  } else if (!loaded) {
    body = (
      <p className="text-center text-sm text-slate-400">Gathering the stars…</p>
    );
  } else if (error) {
    body = (
      <Notice>
        Couldn't load your order history right now. Try again in a moment.
      </Notice>
    );
  } else if (orders.length === 0) {
    body = (
      <Notice>No orders yet. Head to the store and call something in.</Notice>
    );
  } else {
    body = (
      <div className="space-y-12">
        {orders.map((order, i) => (
          <CosmicReceipt
            key={order.id}
            lines={order.lines || []}
            dateLabel={formatWhen(order.createdAt)}
            orderNo={`UNI-${order.id.slice(0, 5).toUpperCase()}`}
            tilt={i % 2 === 0 ? -1.5 : 1.3}
          />
        ))}
      </div>
    );
  }

  return (
    <ManifestShell>
      <div className="mx-auto w-full max-w-3xl px-4 py-12 sm:py-16">
        <header className="mb-10 text-center">
          <p className="text-[11px] uppercase tracking-[0.35em] text-slate-400">
            universe &amp; co. · records department
          </p>
          <h1 className="mt-3 font-serif text-3xl font-semibold sm:text-4xl">
            <span className="ms-shimmer">The receipt drawer</span>
          </h1>
          <p className="mt-3 text-sm text-slate-400">
            Everything you've called in, stamped and filed.
          </p>
          <Link
            to="/manifest"
            className="mt-4 inline-block text-xs text-amber-300/80 underline-offset-4 transition-colors hover:text-amber-200 hover:underline"
          >
            ← Back to the store
          </Link>
        </header>
        {body}
      </div>
    </ManifestShell>
  );
};

export default ManifestLog;
